import React, { useState, useEffect } from "react";
import autoTable from "jspdf-autotable";
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";

import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Typography,
  message,
  Spin,
  Alert,
  Space,
  Image,
  Tag,
  Tooltip
} from "antd";
import {
  DownloadOutlined,
  PlusOutlined,
  FileTextOutlined,
  SearchOutlined,
  UserOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  EditOutlined,
  DatabaseOutlined,
  FilePdfOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import vendorSeal from "../assets/seal_nms.jpeg";
import vendorSign from "../assets/vendor_sign.png";
import api from "../services/api";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const MSEBdocsPage = () => {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [aadhaarImageUrl, setAadhaarImageUrl] = useState("");
  const [aadhaarImageUrls, setAadhaarImageUrls] = useState([]);
  const navigate = useNavigate();

  // Fetch customers with their orders
  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const data = await api.get("/customers?limit=1000");
      if (data.success && Array.isArray(data.data)) {
        setCustomers(data.data);
      } else {
        console.error("Unexpected format:", data);
        setCustomers([]);
      }
    } catch (error) {
      console.error("Network error:", error);
      message.error("Failed to fetch customers");
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Filter customers based on search
  const filteredCustomers = customers.filter((customer) => {
    if (!searchCustomer) return true;
    const search = searchCustomer.toLowerCase();
    return (
      customer.customerName?.toLowerCase().includes(search) ||
      customer.consumerNo?.toLowerCase().includes(search) ||
      customer.customerMobile?.includes(search) ||
      customer.customerEmail?.toLowerCase().includes(search)
    );
  });

  // Parse wattage from model number
  const parseWattage = (modelNo) => {
    if (!modelNo) return 540; // Default to 540W as per document
    const match = modelNo.match(/(\d+)\s*WP?/i);
    return match ? parseInt(match[1]) : 540;
  };

  // Parse capacity from inverter model
  const parseInverterCapacity = (modelNo) => {
    if (!modelNo) return "";

    const patterns = [
      /(\d+(\.\d+)?)\s*KW/i,
      /(\d+(\.\d+)?)\s*K\s*W/i,
      /^(\d+(\.\d+)?)\s*/,
      /[^\d]*(\d+(\.\d+)?)[^\d]*$/
    ];

    for (const pattern of patterns) {
      const match = modelNo.match(pattern);
      if (match) return match[1];
    }

    return "";
  };

  // Get connection type display
  const getConnectionTypeDisplay = (connectionType) => {
    switch (connectionType) {
      case "SinglePhase":
        return "230 V Single Phase";
      case "ThreePhase":
        return "415 V Three Phase";
      case "Mixed":
        return "Mixed Phase";
      default:
        return "230 V Single Phase";
    }
  };

  // Get category from customer data
  const getCategoryFromCustomer = (customer) => {
    if (customer.locationType === "Rural") return "Rural";
    if (customer.locationType === "Urban") return "Private";
    if (customer.houseOwnership === "Single") return "Individual";
    if (customer.houseOwnership === "Joint") return "Joint";
    return "Private";
  };

  // Fetch customer details with orders and items
  const fetchCustomerDetails = async (customerId) => {
    try {
      console.log(`🔍 DEBUG: Calling API for customer ID: ${customerId}`);

      // Fetch customer with orders, items, and assets
      const data = await api.get(`/customers/${customerId}?include=orders.items.item,assets.item`);

      if (data.success && data.data) {
        console.log("✅ DEBUG: Customer found:", data.data.customerName);
        console.log("✅ DEBUG: Orders count:", data.data.orders?.length || 0);
        console.log("✅ DEBUG: Assets count:", data.data.assets?.length || 0);
        return data.data;
      } else {
        console.log("❌ DEBUG: API returned success:false or no data");
      }
    } catch (error) {
      console.error("❌ DEBUG: Error fetching customer details:", error);
    }
    return null;
  };

  // Enhanced function to extract details from Assets with focus on inverter
  const extractAvailableOrderDetails = (order, customerAssets = []) => {
    const details = {
      moduleMake: "",
      moduleModel: "", // ✅ ALMM is user input ONLY. We will NOT auto-fill this anywhere.
      moduleWattage: "",
      numberOfModules: "",
      totalCapacity: "",
      warrantyDetails: "",
      inverterModel: "",
      inverterCapacity: "",
      inverterRating: "",
      mppt: "1 MPPT",
      hpd: "Yes",
      manufacturingYear: "",
      chargeControllerType: "MPPT",
      panelWarrantyYears: order.panelWarrantyYears?.toString() || "",
      inverterWarrantyYears: order.inverterWarrantyYears?.toString() || "",
      plantWarrantyYears: order.plantWarrantyYears?.toString() || ""
    };

    console.log(`🔍 Extracting available details from ${customerAssets.length} assets...`);

    // Get all assets for this order/customer
    const orderAssets = customerAssets.filter(
      (asset) => asset.orderId === order.id || asset.customerId === order.customerId
    );

    console.log(`📊 Found ${orderAssets.length} assets for this order/customer`);

    // === PROCESS ORDER ITEMS FOR BASIC INFORMATION ===
    if (order.items && order.items.length > 0) {
      console.log(`🔍 Processing ${order.items.length} order items`);

      // Find inverter items
      const inverterItems = order.items.filter(
        (item) =>
          item.item?.itemType === "Inverter" ||
          item.item?.itemName?.toLowerCase().includes("inverter")
      );

      // Find solar panel items
      const solarItems = order.items.filter(
        (item) =>
          item.item?.itemType === "SolarPanel" ||
          item.item?.itemName?.toLowerCase().includes("solar") ||
          item.item?.itemName?.toLowerCase().includes("panel")
      );

      // === PROCESS SOLAR PANEL ITEMS ===
      if (solarItems.length > 0) {
        console.log(`✅ Found ${solarItems.length} solar panel items`);

        const solarItem = solarItems[0];
        const itemData = solarItem.item || {};

        // Module Make comes from Serialized Asset brand (not item name)

        // ALMM Model No from Products & Items modelNo
        if (itemData.modelNo && !details.moduleModel) {
          details.moduleModel = itemData.modelNo;
        }

        // Wattage from item name or model number
        if (!details.moduleWattage) {
          let wattage = 540;

          if (itemData.modelNo) {
            const wattageMatch = itemData.modelNo.match(/(\d+)\s*WP?/i);
            if (wattageMatch) wattage = parseInt(wattageMatch[1]);
          }

          if (itemData.itemName && wattage === 540) {
            const wattageMatch = itemData.itemName.match(/(\d+)\s*W/);
            if (wattageMatch) wattage = parseInt(wattageMatch[1]);
          }

          details.moduleWattage = wattage.toString();
          console.log(`✅ Auto-filled moduleWattage: ${wattage}W`);
        }
      }

      // === PROCESS INVERTER ITEMS (fallback only) ===
      if (inverterItems.length > 0) {
        console.log(`✅ Found ${inverterItems.length} inverter items`);

        const inverterItem = inverterItems[0];
        const itemData = inverterItem.item || {};

        let inverterModelStr = "";

        if (itemData.itemName) {
          inverterModelStr = itemData.itemName;

          const capacityMatch = itemData.itemName.match(/(\d+(\.\d+)?)\s*KW/i);
          if (capacityMatch) {
            details.inverterCapacity = capacityMatch[1];
            console.log(`✅ Auto-filled inverterCapacity from itemName: ${capacityMatch[1]}KW`);
          }
        }

        if (itemData.modelNo) {
          inverterModelStr = inverterModelStr
            ? `${inverterModelStr} - ${itemData.modelNo}`
            : itemData.modelNo;

          if (!details.inverterCapacity) {
            const kwMatch = (itemData.modelNo || "").match(/(\d+(\.\d+)?)\s*KW/i);
            if (kwMatch) {
              details.inverterCapacity = kwMatch[1];
              console.log(`✅ Auto-filled inverterCapacity from modelNo KW match: ${kwMatch[1]}KW`);
            }
          }
        }

        details.inverterModel = inverterModelStr;
      }
    }

    // === PROCESS ASSETS FOR MORE SPECIFIC DETAILS ===

    // Filter for inverter assets
    const inverterAssets = orderAssets.filter((asset) => {
      if (asset.item && asset.item.itemType === "Inverter") return true;

      // NOTE: brand check is weak, but keeping as fallback
      if (asset.brand && asset.brand.toLowerCase().includes("inverter")) return true;

      return false;
    });

    // Filter for solar panel assets
    const solarPanelAssets = orderAssets.filter((asset) => {
      if (asset.item && asset.item.itemType === "SolarPanel") return true;

      if (
        asset.brand &&
        (asset.brand.toLowerCase().includes("solar") ||
          asset.brand.toLowerCase().includes("premier") ||
          asset.brand.toLowerCase().includes("panel"))
      ) {
        return true;
      }

      return false;
    });

    // === PROCESS INVERTER ASSETS (PRIMARY SOURCE FOR Make & Model) ===
    if (inverterAssets.length > 0) {
      console.log(`✅ Found ${inverterAssets.length} inverter assets`);
      console.log("🔎 inverterAssets sample (first):", inverterAssets[0]);
      console.log("🔎 fields:", {
        itemName: inverterAssets[0]?.item?.itemName,
        itemModelNo: inverterAssets[0]?.item?.modelNo,
        serialNumber: inverterAssets[0]?.serialNumber,
        batchId: inverterAssets[0]?.batchId,
        invoiceNumber: inverterAssets[0]?.invoiceNumber,
        brand: inverterAssets[0]?.brand,
        capacity: inverterAssets[0]?.capacity,
      });

      const makeModelList = inverterAssets
        .map((a) => {
          // Brand from serialized asset = Make (manufacturer)
          const make = a?.brand?.trim() || "";

          // Model from linked item
          const model =
            a?.item?.itemName?.trim() ||
            a?.item?.modelNo?.trim() ||
            "";

          // Serial number
          const serial = a?.serialNumber?.trim() || "";

          // Format: "ItemName   SerialNumber"
          return [model, serial].filter(Boolean).join("   ");
        })
        .filter(Boolean);

      if (makeModelList.length > 0) {
        details.inverterModel = makeModelList.join(", ");
        console.log(`✅ Auto-filled inverterModel from asset.item: ${details.inverterModel}`);
      }

      const inverterAsset = inverterAssets[0];

      // manufacturingYear — user enters manually, not auto-populated

      // Capacity from serialized asset's capacity field
      if (!details.inverterCapacity && inverterAsset?.capacity != null && inverterAsset.capacity !== 0) {
        details.inverterCapacity = String(inverterAsset.capacity);
        console.log(`✅ Auto-filled inverterCapacity from asset.capacity: ${details.inverterCapacity}`);
      }
      // Fallback: parse KW from item name only
      if (!details.inverterCapacity) {
        const kwMatch = (inverterAsset?.item?.itemName || "").match(/(\d+(\.\d+)?)\s*KW/i);
        if (kwMatch) {
          details.inverterCapacity = kwMatch[1];
          console.log(`✅ Auto-filled inverterCapacity from itemName KW match: ${kwMatch[1]}`);
        }
      }
    }

    // === PROCESS SOLAR PANEL ASSETS ===
    if (solarPanelAssets.length > 0) {
      console.log(`✅ Found ${solarPanelAssets.length} solar panel assets`);

      const solarPanelAsset = solarPanelAssets.find((asset) => asset.brand) || solarPanelAssets[0];

      if (solarPanelAsset?.brand && solarPanelAsset.brand.length > 2) {
        details.moduleMake = solarPanelAsset.brand;
        console.log(`✅ Auto-filled moduleMake from asset: ${solarPanelAsset.brand}`);
      }

      // ALMM Model No from asset's linked item modelNo
      if (!details.moduleModel) {
        const assetWithModel = solarPanelAssets.find(a => a.item?.modelNo);
        if (assetWithModel?.item?.modelNo) {
          details.moduleModel = assetWithModel.item.modelNo;
        }
      }

      // Wattage per module from SerializedAsset.capacity (MSEDCL WCR - primary source)
      const assetWithCapacity = solarPanelAssets.find((a) => a.capacity != null && a.capacity !== "");
      const sourceAsset = assetWithCapacity || solarPanelAsset;
      if (sourceAsset?.capacity != null && sourceAsset?.capacity !== "") {
        const cap = parseInt(String(sourceAsset.capacity), 10);
        if (Number.isFinite(cap) && cap > 0) {
          details.moduleWattage = String(cap);
          console.log(`✅ Auto-filled moduleWattage from SerializedAsset.capacity: ${details.moduleWattage}W`);
        }
      }

      // Number of solar panel assets
      details.numberOfModules = solarPanelAssets.length.toString();
      console.log(`✅ Auto-filled numberOfModules: ${solarPanelAssets.length}`);

      // manufacturingYear — user enters manually, not auto-populated
    }

    // === SET NA IF NO ASSET DATA FOUND ===
    if (!details.inverterModel) details.inverterModel = "NA";
    if (!details.inverterCapacity) details.inverterCapacity = "NA";
    if (!details.moduleWattage) details.moduleWattage = "NA";

    // Calculate total capacity AFTER defaults are applied
    if (details.numberOfModules) {
      const wattage = parseInt(details.moduleWattage) || 540;
      const modules = parseInt(details.numberOfModules) || 1;
      const totalCapacityKW = (modules * wattage) / 1000;
      details.totalCapacity = totalCapacityKW.toFixed(3);
    }

    // Inverter Rating from order.connectionType
    if (order.connectionType && !details.inverterRating) {
      details.inverterRating = getConnectionTypeDisplay(order.connectionType);
    }

    // Warranty details from order (show even if 0 years)
    const pW = order.panelWarrantyYears ?? 0;
    const iW = order.inverterWarrantyYears ?? 0;
    const plW = order.plantWarrantyYears ?? 0;
    if (pW || iW || plW) {
      details.warrantyDetails = `Panel: ${pW} years, Inverter: ${iW} years, Plant: ${plW} years`;
    }

    console.log(
      `📋 Final extracted details:`,
      Object.entries(details)
        .filter(([key, value]) => value && value !== "")
        .map(([key, value]) => `${key}: "${value}"`)
    );

    return details;
  };

  const FieldStatus = ({ fieldName }) => {
    const value = form.getFieldValue(fieldName);

    if (!value || value.toString().trim() === "") {
      return (
        <Tooltip title="Please fill this field">
          <WarningOutlined style={{ color: "#faad14", marginLeft: 8, fontSize: "12px" }} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Field has data">
        <CheckCircleOutlined style={{ color: "#52c41a", marginLeft: 8, fontSize: "12px" }} />
      </Tooltip>
    );
  };

  // Helper to check if field has value
  const hasValue = (fieldName) => {
    const value = form.getFieldValue(fieldName);
    return value && value.toString().trim() !== "";
  };

  // Handle customer selection
  const handleCustomerChange = async (customerId) => {

    console.log(`🎯 Customer selected with ID: ${customerId}`);
    setLoading(true);
    try {
      const customer = customers.find((c) => c.id === customerId);

      if (!customer) {
        message.error("Customer not found");
        return;
      }

      const detailedCustomer = await fetchCustomerDetails(customerId);
      const finalCustomer = detailedCustomer || customer;

      setSelectedCustomer(finalCustomer);

      const orders = finalCustomer.orders || [];
      setCustomerOrders(orders);
      console.log("🧾 Orders received:", orders);
      console.log("🧾 Sanction numbers:", orders.map(o => ({
        id: o.id,
        sanctionNumber: o.sanctionNumber,
        createdAt: o.createdAt
      })));


      // Select the most recent order that has a sanctionNumber (fallback: latest order)
      let latestOrder = null;

      if (orders.length > 0) {
        const ordersSorted = [...orders].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        latestOrder =
          ordersSorted.find(
            (o) => o?.sanctionNumber && o.sanctionNumber.toString().trim() !== ""
          ) || ordersSorted[0];
      }


      setSelectedOrder(latestOrder);

      // Prepare form values
      const formValues = {
        consumerNumber: finalCustomer.consumerNo || finalCustomer.consumerNumbers?.[0]?.consumerNumber || "",
        name: finalCustomer.customerName || "",
        address: finalCustomer.customerAddress || "",
        district: finalCustomer.district || "",
        state: "Maharashtra",
        pincode: finalCustomer.pincode || finalCustomer.pinCode || finalCustomer.postalCode || "",
        category: getCategoryFromCustomer(finalCustomer), // Auto-select based on location/ownership
        aadharNumber: finalCustomer.aadharNo || "",

        numberOfEarthings: "",
        earthingResistance: "",
        lightningArrester: "",

        vendorName: "Niyati Multi Services",

        sanctionNumber: "",
        selectedConsumerNumber: "",

        sanctionedCapacity: "",
        installedCapacity: "",
        moduleMake: "",
        moduleModel: "", // ✅ ALMM user input (start empty on new customer)
        moduleWattage: "",
        numberOfModules: "",
        totalCapacity: "",
        warrantyDetails: "",
        inverterModel: "",
        inverterCapacity: "",
        inverterRating: "",
        mppt: "1 MPPT",
        hpd: "Yes",
        manufacturingYear: "",
        chargeControllerType: "MPPT",
        panelWarrantyYears: "",
        inverterWarrantyYears: "",
        plantWarrantyYears: ""
      };

      if (latestOrder) {
        console.log(`🎯 Populating available data from order...`);

        formValues.sanctionedCapacity = latestOrder.plantSizeKw?.toString() || "3";
        formValues.installedCapacity = latestOrder.plantSizeKw?.toString() || "3";
        formValues.sanctionNumber = latestOrder.sanctionNumber || "";
        formValues.selectedConsumerNumber = latestOrder.selectedConsumerNumber || "";
        if (latestOrder.selectedConsumerNumber) {
          formValues.consumerNumber = latestOrder.selectedConsumerNumber;
        }

        formValues.panelWarrantyYears = latestOrder.panelWarrantyYears?.toString() || "";
        formValues.inverterWarrantyYears = latestOrder.inverterWarrantyYears?.toString() || "";
        formValues.plantWarrantyYears = latestOrder.plantWarrantyYears?.toString() || "";

        const availableDetails = extractAvailableOrderDetails(latestOrder, finalCustomer.assets || []);
        Object.assign(formValues, availableDetails);

        formValues.inverterRating = getConnectionTypeDisplay(latestOrder.connectionType);
      }

      form.setFieldsValue(formValues);

      if (finalCustomer?.aadharFilePath) {
        // Handle both single string and JSON array of strings
        let paths = [];
        try {
          if (finalCustomer.aadharFilePath.startsWith('[')) {
            paths = JSON.parse(finalCustomer.aadharFilePath);
          } else if (finalCustomer.aadharFilePath) {
            paths = [finalCustomer.aadharFilePath];
          }
        } catch (e) {
          paths = [finalCustomer.aadharFilePath];
        }

        const urls = paths.map(p => {
          const path = String(p);
          return path.startsWith('http') 
            ? path 
            : `${api.defaults.baseURL.replace(/\/api\/?$/, "")}/api/${path.replace(/^\/+/, "")}`;
        });
          
        console.log("DEBUG WCR - aadharPaths:", paths);
        setAadhaarImageUrl(urls.length > 0 ? urls[0] : ""); // Main preview using first image
        setAadhaarImageUrls(urls); // New state for multiple images
      } else {
        setAadhaarImageUrl("");
        setAadhaarImageUrls([]);
      }

    } catch (error) {
      console.error("Error loading customer:", error);
      message.error("Error loading customer and order details");
    } finally {
      setLoading(false);
    }
  };

  // Handle order selection change
  const handleOrderChange = (orderId) => {
    const order = customerOrders.find((o) => o.id === orderId);
    if (order) {
      setSelectedOrder(order);

      const updates = {
        sanctionedCapacity: order.plantSizeKw?.toString() || "3",
        installedCapacity: order.plantSizeKw?.toString() || "3",
        sanctionNumber: order.sanctionNumber || "",
        selectedConsumerNumber: order.selectedConsumerNumber || "",
        inverterRating: getConnectionTypeDisplay(order.connectionType),
        panelWarrantyYears: order.panelWarrantyYears?.toString() || "",
        inverterWarrantyYears: order.inverterWarrantyYears?.toString() || ""
      };

      if (order.selectedConsumerNumber) {
        updates.consumerNumber = order.selectedConsumerNumber;
      }

      const availableDetails = extractAvailableOrderDetails(order, selectedCustomer?.assets || []);
      Object.assign(updates, availableDetails);

      form.setFieldsValue(updates);

      message.info(`Switched to Order #${order.id} - ${order.plantSizeKw || 0}KW`);
    }
  };
  // helper to convert image url to data url (needed for jsPDF)
  const imageUrlToDataUrl = async (url) => {
    try {
      if (!url) return null;
      
      let fullUrl = url;
      
      // Smart detection for different image sources
      if (url.startsWith('http') || url.startsWith('data:')) {
        // Absolute or data URL - use it directly
        fullUrl = url;
      } else if (url.includes('src/assets') || url.includes('assets/') || url.startsWith('/')) {
        // Local Frontend Asset - use it directly from origin
        fullUrl = url;
      } else {
        // Backend Upload - prepend backend base URL
        fullUrl = `${api.defaults.baseURL.replace(/\/api\/?$/, "")}/api/${url.replace(/^\/+/, "")}`;
      }

      console.log("DEBUG WCR - Fetching image from:", fullUrl);
      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("imageUrlToDataUrl error:", err, "for url:", url);
      return null;
    }
  };

  // PDF generation function matching the Word document format
  // ============================================================
  // Generate WCR as .docx using template
  // ============================================================
  const generateDocx = async (formData) => {
    try {
      // 1. Load the template
      const response = await fetch("/templates/WCR_template_v2.docx");
      if (!response.ok) throw new Error("Failed to load WCR template");
      const templateArrayBuffer = await response.arrayBuffer();

      // 2. Fetch Aadhaar images as ArrayBuffers for the image module
      const aadhaarUrls = aadhaarImageUrls.length > 0
        ? aadhaarImageUrls
        : (formData.aadhaarImageUrl ? [formData.aadhaarImageUrl] : []);

      const resolveImgUrl = (imgUrl) => {
        const isPdf = String(imgUrl).toLowerCase().includes(".pdf");
        if (isPdf && imgUrl.includes("cloudinary.com")) {
          imgUrl = imgUrl.replace(/\.pdf$/i, ".jpg");
          if (imgUrl.includes("/upload/")) {
            imgUrl = imgUrl.replace("/upload/", "/upload/pg_1/");
          }
        }
        if (!imgUrl.startsWith("http") && !imgUrl.startsWith("data:")) {
          if (!imgUrl.startsWith("/") && !imgUrl.includes("assets/")) {
            imgUrl = `${api.defaults.baseURL.replace(/\/api\/?$/, "")}/api/${imgUrl.replace(/^\/+/, "")}`;
          }
        }
        return imgUrl;
      };

      const fetchImageBuffer = async (url) => {
        try {
          const resolved = resolveImgUrl(url);
          console.log("WCR DOCX - Fetching Aadhaar image:", resolved);
          const res = await fetch(resolved);
          if (res.ok) return await res.arrayBuffer();
        } catch (err) {
          console.error("WCR DOCX - Failed to load image:", err);
        }
        return null;
      };

      const aadharImageBuffer  = aadhaarUrls[0] ? await fetchImageBuffer(aadhaarUrls[0]) : null;
      const aadharImageBuffer2 = aadhaarUrls[1] ? await fetchImageBuffer(aadhaarUrls[1]) : null;

      // 3. Configure image module
      const imageOpts = {
        centered: false,
        getImage: (tagValue) => {
          if (tagValue === "aadhar"  && aadharImageBuffer)  return aadharImageBuffer;
          if (tagValue === "aadhar2" && aadharImageBuffer2) return aadharImageBuffer2;
          return new ArrayBuffer(0);
        },
        getSize: () => [400, 250], // width x height in pixels
      };
      const imageModule = new ImageModule(imageOpts);

      // 4. Parse template with PizZip + image module
      const zip = new PizZip(templateArrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [imageModule],
      });

      // 5. Build full address
      const addressParts = [
        formData.address,
        formData.district ? `District: ${formData.district}` : "",
        formData.state ? `State: ${formData.state}` : "State: Maharashtra",
        formData.pincode ? `Pin Code: ${formData.pincode}` : "",
      ].filter(Boolean);
      const fullAddress = addressParts.join(", ");

      // 6. Fill template data
      doc.render({
        customerName: formData.name || "",
        consumerNumber: formData.consumerNumber || "",
        consumerNumber2: formData.consumerNumber || "",
        address: fullAddress,
        category: formData.category || "Private",
        sanctionNumber: formData.sanctionNumber || "",
        sanctionedCapacity: formData.sanctionedCapacity ? `${formData.sanctionedCapacity} KW` : "",
        installedCapacity: `${formData.installedCapacity || formData.sanctionedCapacity || ""} KW`,
        moduleMake: formData.moduleMake || "",
        moduleModel: formData.moduleModel || "",
        moduleWattage: formData.moduleWattage ? `${formData.moduleWattage} WP` : "",
        numberOfModules: formData.numberOfModules || "",
        totalCapacity: formData.totalCapacity || "",
        warrantyDetails: formData.warrantyDetails || "",
        inverterModel: formData.inverterModel || "",
        inverterRating: formData.inverterRating || "230 V Single Phase",
        mppt: formData.mppt || "1 MPPT",
        inverterCapacity: formData.inverterCapacity ? `${formData.inverterCapacity} KW` : "",
        hpd: formData.hpd || "Yes",
        manufacturingYear: formData.manufacturingYear || "",
        earthingDetails: formData.numberOfEarthings || "3",
        lightningArrester: formData.lightningArrester || "Yes",
        vendorName: formData.vendorName || "Niyati Multi Services",
        aadharNumber: formData.aadharNumber || formData.aadharNo || "",
        aadharImage: aadharImageBuffer ? "aadhar" : "",
        aadharImage2: aadharImageBuffer2 ? "aadhar2" : "",
      });

      // 7. Generate and download
      const output = doc.getZip().generate({
        type: "blob",
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const fileName = `WCR_${(formData.name || "Customer").replace(/\s+/g, "_")}_${dayjs().format("DDMMYYYY")}.docx`;
      saveAs(output, fileName);

      message.success(`WCR document generated: ${fileName}`);
      return true;
    } catch (error) {
      console.error("DOCX generation error:", error);
      message.error("Failed to generate WCR document: " + error.message);
      return false;
    }
  };

  const generatePDF = async (formData) => {
    try {
      const doc = new jsPDF();
      let yPos = 20;

      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Work Completion Report for Solar Power Plant", 105, yPos, { align: "center" });

      yPos += 10;

      const tableConfig = {
        startY: yPos,
        head: [["Sr.No.", "Component", "Observation"]],
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
          valign: "middle"
        },
        styles: {
          fontSize: 8,
          cellPadding: 2,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          fontStyle: "normal",
          textColor: [0, 0, 0],
          overflow: "linebreak",
          cellWidth: "auto"
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center" },
          1: { cellWidth: 60, halign: "left" },
          2: { cellWidth: 115, halign: "left" }
        },
        margin: { left: 10 }
      };

      const sectionHeaderStyle = {
        fontStyle: "bold",
        halign: "center",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0]
      };

      const boldCell = (content) => ({ content, styles: { fontStyle: "bold" } });

      const tableData = [
        // Row 1
        ["1", "Name", boldCell(formData.name || "")],
        // Row 2
        ["2", "Consumer number", boldCell(formData.consumerNumber || "")],
        // Row 3
        [
          "3",
          "Site/Location with Complete Address",
          boldCell(`${formData.address || ""},\nDistrict: ${formData.district || ""}, State: ${formData.state || "Maharashtra"},\nPin Code: ${formData.pincode || ""}`)
        ],
        // Row 4
        ["4", "Category: Govt/Private Sector", boldCell(formData.category || "Private")],
        // Row 5
        ["5", "Sanction number", boldCell(formData.sanctionNumber || "")],
        // Row 6 – main + sub-row
        ["6", "Sanctioned Capacity of solar PV system (KW) Installed", `${formData.sanctionedCapacity || "3"} KW`],
        ["",  "Capacity of solar PV system (KW)", `${formData.installedCapacity || "3"} KW`],
        // Section header
        [{ content: "Specification of the Modules", colSpan: 3, styles: sectionHeaderStyle }],
        // Row 7 – main + sub-rows
        ["7", "Make of Module", formData.moduleMake || ""],
        ["",  "ALMM Model Number", formData.moduleModel || ""],
        ["",  "Wattage per module", formData.moduleWattage ? `${formData.moduleWattage} WP` : "540 WP"],
        ["",  "No. of Module", formData.numberOfModules || "6"],
        ["",  "Total Capacity (KWP)", formData.totalCapacity || ""],
        ["",  "Warrantee Details (Product + Performance)", formData.warrantyDetails || ""],
        // Section header
        [{ content: "PCU", colSpan: 3, styles: sectionHeaderStyle }],
        // Row 8 – main + sub-rows
        ["8", "Make & Model number of Inverter", formData.inverterModel || ""],
        ["",  "Rating", formData.inverterRating || "230 V Single Phase"],
        ["",  "Type of charge controller/ MPPT", formData.mppt || "1 MPPT"],
        ["",  "Capacity of Inverter", formData.inverterCapacity ? `${formData.inverterCapacity} KW` : ""],
        ["",  "HPD", formData.hpd || "Yes"],
        ["",  "Year of manufacturing", formData.manufacturingYear || ""],
        // Section header
        [{ content: "Earthing and Protections", colSpan: 3, styles: sectionHeaderStyle }],
        // Row 9 – main + certification + sub-row
        ["9", "No of Separate Earthings with earth Resistance", formData.numberOfEarthings || "3"],
        [
          "",
          {
            content: "It is certified that the Earth Resistance measure in presence of Licensed Electrical Contractor/Supervisor and found in order i.e. < 5 Ohms as per MNRE OM Dtd. 04.01.25 for CFA Component.",
            colSpan: 2,
            styles: { fontStyle: "italic", halign: "left" }
          }
        ],
        ["",  "Lightening Arrester", formData.lightningArrester || "Yes"]
      ];

      autoTable(doc, {
        ...tableConfig,
        body: tableData
      });


      const finalY = doc.lastAutoTable.finalY + 5;

      doc.setFontSize(8.5);
      doc.setFont("helvetica", "normal");

      let declarationY = finalY;

      const vendorName = formData.vendorName || "Niyati Multi Services";
      const consumerName = formData.name || "";
      const consumerNo = formData.consumerNumber || "";

      // Helper: render inline text with mixed bold/normal segments, returns final Y
      const renderInlineBold = (segments, startX, startY, maxWidth, lineHeight) => {
        const words = [];
        for (const seg of segments) {
          const parts = seg.text.split(/(\s+)/);
          for (const p of parts) { if (p) words.push({ text: p, bold: seg.bold }); }
        }
        const lines = [[]];
        let lineW = 0;
        for (const word of words) {
          doc.setFont("helvetica", word.bold ? "bold" : "normal");
          const ww = doc.getTextWidth(word.text);
          if (lineW + ww > maxWidth && lineW > 0 && word.text.trim()) {
            lines.push([]);
            lineW = 0;
          }
          lines[lines.length - 1].push(word);
          lineW += ww;
        }
        let y = startY;
        for (const line of lines) {
          let x = startX;
          for (const word of line) {
            doc.setFont("helvetica", word.bold ? "bold" : "normal");
            doc.text(word.text, x, y);
            x += doc.getTextWidth(word.text);
          }
          y += lineHeight;
        }
        doc.setFont("helvetica", "normal");
        return y;
      };

      // Declaration 1 — vendor name, consumer name, consumer no in bold
      declarationY = renderInlineBold([
        { text: "We ", bold: false },
        { text: vendorName, bold: true },
        { text: " [Vendor] & ", bold: false },
        { text: consumerName, bold: true },
        { text: " [Consumer] bearing Consumer Number ", bold: false },
        { text: consumerNo, bold: true },
        { text: " Ensured structural stability of installed solar power plant and obtained requisite permissions from the concerned authority. If in future, by virtue of any means due to collapsing or damage to installed solar power plant, MSEDCL will not be held responsible for any loss to property or human life, if any.", bold: false }
      ], 10, declarationY, 190, 4.5);

      declarationY += 3;

      const declaration2Lines = doc.splitTextToSize(
        `This is to certified above Installed Solar PV System is working properly with electrical safety & Islanding switch in case of any presence of backup inverter an arrangement should be made in such way the backup inverter supply should never be synchronized with solar inverter to avoid any electrical accident due to back feeding. We will be held responsible for non-working of islanding mechanism and back feed to the de-energized grid.`,
        190
      );

      doc.text(declaration2Lines, 10, declarationY);
      declarationY += declaration2Lines.length * 4.5;

      let signatureY = declarationY + 12;

      // Add Vendor Signature
      try {
        const signData = await imageUrlToDataUrl(vendorSign);
        doc.addImage(signData, "PNG", 30, signatureY - 12, 35, 12);
      } catch (err) {
        console.error("Failed to add vendor signature:", err);
      }

      doc.text("Signature [Vendor]", 40, signatureY);
      doc.text("Signature [Consumer]", 130, signatureY);

      doc.addPage();

      let guaranteeY = 20;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Guarantee Certificate Undertaking to be submitted by VENDOR", 105, guaranteeY, { align: "center" });

      guaranteeY += 10;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");

      const guaranteeText = [
        `The undersigned will provide the services to the consumers for repairs/maintenance of the RTS plant free of cost for 5 years of the`,
        `comprehensive Maintenance Contract (CMC) period from the date of commissioning of the plant. Non performing/under-performing system`,
        `component will be replaced/repaired free of cost in the CMC period`
      ];

      guaranteeText.forEach((line, index) => {
        doc.text(line, 10, guaranteeY + index * 5);
      });

      guaranteeY += 25;

      // Add Vendor Signature and Seal on Page 2
      try {
        const signData = await imageUrlToDataUrl(vendorSign);
        const sealData = await imageUrlToDataUrl(vendorSeal);

        doc.addImage(signData, "PNG", 30, guaranteeY - 12, 35, 12);
        doc.addImage(sealData, "JPEG", 125, guaranteeY - 20, 30, 30);
      } catch (err) {
        console.error("Failed to add signature/seal on page 2:", err);
      }

      doc.text("Signature [Vendor]", 40, guaranteeY);
      doc.text("Stamp & Seal", 130, guaranteeY);

      guaranteeY += 15;
      
      // Identity Details section
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Identity Details of Consumer: -", 10, guaranteeY);

      // Aadhaar Details
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Identity Details of Consumer: -", 10, guaranteeY);

      guaranteeY += 7;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Aadhar Number: ${formData.aadharNumber || formData.aadharNo || ""}`, 10, guaranteeY);

      // Loop through multiple Aadhaar images
      const aadhaarUrls = aadhaarImageUrls.length > 0 ? aadhaarImageUrls : (formData.aadhaarImageUrl ? [formData.aadhaarImageUrl] : []);
      
      for (let i = 0; i < aadhaarUrls.length; i++) {
        const url = aadhaarUrls[i];
        try {
          const isPdf = String(url).toLowerCase().includes(".pdf");
          let embedUrl = url;
          
          if (isPdf && embedUrl.includes('cloudinary.com')) {
            embedUrl = embedUrl.replace(/\.pdf$/i, '.jpg');
            if (embedUrl.includes('/upload/')) {
              embedUrl = embedUrl.replace('/upload/', '/upload/pg_1/');
            }
          }

          if (!isPdf || (isPdf && embedUrl.includes('cloudinary.com'))) {
            guaranteeY += 8;
            doc.setFont("helvetica", "bold");
            doc.text(`Aadhaar Image ${aadhaarUrls.length > 1 ? i + 1 : ""}:`, 10, guaranteeY);
            doc.setFont("helvetica", "normal");

            const dataUrl = await imageUrlToDataUrl(embedUrl);
            if (dataUrl) {
              const format = String(dataUrl).startsWith("data:image/png") ? "PNG" : "JPEG";
              const imgW = 90;
              const imgH = 55;
              
              // If we run out of space on current page, add new page
              if (guaranteeY + imgH > 280) {
                doc.addPage();
                guaranteeY = 15;
              }

              doc.addImage(dataUrl, format, 10, guaranteeY + 4, imgW, imgH);
              guaranteeY = guaranteeY + imgH + 10;
            }
          }
        } catch (err) {
          console.error("Aadhaar embed failed for index", i, err);
        }
      }


      const fileName = `WCR_${(formData.name || "Customer").replace(/\s+/g, "_")}_${dayjs().format("DDMMYYYY")}.pdf`;
      doc.save(fileName);

      message.success(`PDF generated successfully: ${fileName}`);
      return true;
    } catch (error) {
      console.error("PDF generation error:", error);
      message.error("Failed to generate PDF: " + error.message);
      return false;
    }
  };

  const handleGeneratePDF = async () => {
    try {
      setLoading(true);

      if (!selectedCustomer) {
        message.warning("Please select a customer first");
        return;
      }

      const formValues = form.getFieldsValue();

      const pdfData = {
        ...formValues,
        aadharFilePath: selectedCustomer.aadharFilePath,
        aadhaarImageUrl: aadhaarImageUrl, // ✅ ADD THIS
        aadharNo: selectedCustomer.aadharNo || formValues.aadharNumber,
        name: selectedCustomer.customerName || formValues.name,
        consumerNumber: selectedOrder?.selectedConsumerNumber || selectedCustomer.consumerNo || formValues.consumerNumber,
        orderId: selectedOrder?.id,
        plantSize: selectedOrder?.plantSizeKw
      };

      await generateDocx(pdfData);
    } catch (error) {
      console.error("Error generating WCR:", error);
      message.error("Failed to generate WCR: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>
            <FileTextOutlined style={{ color: "#1890ff", marginRight: 12 }} />
            MSEDCL Work Completion Report
          </Title>
          <Text type="secondary">Generate official WCR reports with auto-populated data.</Text>
        </div>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => navigate("/customers")}
          style={{ borderRadius: 8 }}
        >
          Add New Customer
        </Button>
      </div>

      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            {/* Left Column: Form Section */}
            <Col xs={24} lg={selectedCustomer ? 10 : 24}>
              {/* Step 1: Customer Selection */}
              <Card
                title={
                  <Space>
                    <div style={{ background: '#e6f7ff', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                      <UserOutlined style={{ color: '#1890ff' }} />
                    </div>
                    <span>Step 1: Select Customer & Order</span>
                  </Space>
                }
                bordered={false}
                style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: 24 }}
              >
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      label="Search Customer"
                      name="customerId"
                      rules={[{ required: true, message: "Please select a customer" }]}
                      extra="Search by name, consumer number, or mobile..."
                    >
                      <Select
                        showSearch
                        placeholder="Type to search..."
                        onChange={handleCustomerChange}
                        onSearch={setSearchCustomer}
                        filterOption={false}
                        loading={loadingCustomers}
                        size="large"
                        style={{ borderRadius: 8 }}
                      >
                        {filteredCustomers.map((customer) => (
                          <Option key={customer.id} value={customer.id}>
                            <div className="flex flex-col py-1">
                              <div className="flex items-center gap-2">
                                <Text strong>{customer.customerName}</Text>
                                {customer.aadharFilePath && <Tag color="green">Aadhaar Available</Tag>}
                              </div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {customer.consumerNo && `Consumer: ${customer.consumerNo} • `}
                                {customer.customerMobile}
                              </Text>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {selectedCustomer && (
                  <div style={{ marginTop: 16, padding: 20, borderRadius: 12, border: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                    <Row gutter={24}>
                      <Col xs={24} md={12}>
                        <Title level={5} style={{ marginTop: 0 }}>Customer Summary</Title>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div className="flex justify-between"><Text type="secondary">Name:</Text> <Text strong>{selectedCustomer.customerName}</Text></div>
                          <div className="flex justify-between"><Text type="secondary">Consumer No:</Text> <Text>{selectedOrder?.selectedConsumerNumber || selectedCustomer.consumerNo || "N/A"}</Text></div>
                          <div className="flex justify-between"><Text type="secondary">Aadhaar:</Text> <Text>{selectedCustomer.aadharNo || "N/A"}</Text></div>
                        </Space>
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5} style={{ marginTop: 0 }}>Select Associated Order</Title>
                        {customerOrders.length > 0 ? (
                          <Form.Item label="Working Order" style={{ marginBottom: 0 }}>
                            <Select
                              placeholder="Choose Order"
                              value={selectedOrder?.id}
                              onChange={handleOrderChange}
                              style={{ width: "100%" }}
                            >
                              {customerOrders.map((order) => (
                                <Option key={order.id} value={order.id}>
                                  Order #{order.id} - {order.plantSizeKw}KW ({order.projectStatus})
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Alert message="No orders found for this customer" type="warning" showIcon />
                        )}
                      </Col>
                    </Row>
                  </div>
                )}
              </Card>

              {selectedCustomer && (
                <>
                  {/* Step 2: User inputs only (auto-populated data is hidden, used for PDF) */}
                  <Card
                    title={
                      <Space>
                        <div style={{ background: '#f6ffed', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                          <DatabaseOutlined style={{ color: '#52c41a' }} />
                        </div>
                        <span>Step 2: WCR Required Inputs</span>
                      </Space>
                    }
                    bordered={false}
                    style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: 24 }}
                  >
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item label="Vendor Name" name="vendorName">
                          <Input placeholder="e.g. Niyati Multi Services" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="ALMM Model Number" name="moduleModel">
                          <Input placeholder="ALMM model number" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="Category" name="category" rules={[{ required: true, message: 'Please select category' }]}>
                          <Select placeholder="Select Government or Private" allowClear>
                            <Option value="Government">Government</Option>
                            <Option value="Private">Private</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="Sanction Number" name="sanctionNumber">
                          <Input placeholder="Sanction number" />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Divider orientation="left" style={{ margin: '8px 0 16px', fontSize: 13 }}>Earthing & Protection</Divider>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="No. of Earthings" name="numberOfEarthings">
                          <Input placeholder="e.g. 3" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="Earth Resistance" name="earthingResistance">
                          <Input placeholder="e.g. < 5 Ohms" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="Lightning Arrester" name="lightningArrester">
                          <Select placeholder="Select">
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="Inverter Year of Manufacturing" name="manufacturingYear">
                          <Input placeholder="e.g. 2024" maxLength={4} />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="Warranty Details" name="warrantyDetails">
                          <TextArea rows={2} placeholder="e.g. Panel: 25 years, Inverter: 5 years, Plant: 5 years" />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Hidden: auto-populated fields (kept for form state / PDF) */}
                    <div style={{ display: 'none' }}>
                      <Form.Item name="consumerNumber" /><Form.Item name="name" /><Form.Item name="address" />
                      <Form.Item name="district" /><Form.Item name="state" /><Form.Item name="pincode" /><Form.Item name="aadharNumber" />
                      <Form.Item name="sanctionedCapacity" /><Form.Item name="installedCapacity" />
                      <Form.Item name="moduleMake" /><Form.Item name="moduleWattage" /><Form.Item name="numberOfModules" /><Form.Item name="totalCapacity" />
                      <Form.Item name="inverterModel" /><Form.Item name="inverterRating" /><Form.Item name="inverterCapacity" /><Form.Item name="mppt" /><Form.Item name="hpd" />
                      <Form.Item name="panelWarrantyYears" /><Form.Item name="inverterWarrantyYears" /><Form.Item name="plantWarrantyYears" />
                    </div>
                  </Card>

                  {/* Final Actions */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 40, padding: 24, background: '#fff', borderRadius: 16, boxShadow: '0 -4px 12px rgba(0,0,0,0.02)' }}>
                    <Button
                      size="large"
                      onClick={() => {
                        form.resetFields();
                        setSelectedCustomer(null);
                      }}
                      style={{ borderRadius: 8, height: 48, padding: '0 24px' }}
                    >
                      Clear All
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      icon={<DownloadOutlined />}
                      onClick={handleGeneratePDF}
                      loading={loading}
                      style={{ borderRadius: 8, height: 48, padding: '0 32px' }}
                    >
                      Generate & Download WCR (.docx)
                    </Button>
                  </div>
                </>
              )}
            </Col>

            {/* Right Column: Live Preview */}
            {selectedCustomer && (
              <Col xs={24} lg={14}>
                <Card
                  title={<Space><FileTextOutlined style={{ color: '#52c41a' }} /> WCR Live Preview</Space>}
                  bordered={false}
                  style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'sticky', top: 24 }}
                >
                  <div style={{ background: '#f0f2f5', padding: '15px', borderRadius: 12, overflowY: 'auto', maxHeight: '100vh' }}>
                    <div style={{
                      padding: '30px',
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '10pt',
                      color: '#000',
                      backgroundColor: '#fff',
                      lineHeight: '1.4',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      width: '100%',
                      minHeight: '297mm'
                    }}>
                      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontWeight: 'bold' }}>Work Completion Report for Solar Power Plant</h3>
                      </div>

                      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #000', padding: '5px', width: '50px' }}>Sr.No.</th>
                            <th style={{ border: '1px solid #000', padding: '5px', width: '180px' }}>Component</th>
                            <th style={{ border: '1px solid #000', padding: '5px' }}>Observation</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>1</td><td style={{ border: '1px solid #000', padding: '5px' }}>Name</td><td style={{ border: '1px solid #000', padding: '5px' }}>{formValues?.name}</td></tr>
                          <tr><td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>2</td><td style={{ border: '1px solid #000', padding: '5px' }}>Consumer number</td><td style={{ border: '1px solid #000', padding: '5px' }}>{formValues?.consumerNumber}</td></tr>
                          <tr>
                            <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>3</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>Site/Location With Complete Address</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>
                              {formValues?.address && <div>{formValues.address}</div>}
                              District: {formValues?.district}, State: {formValues?.state}, Pin Code: {formValues?.pincode}
                            </td>
                          </tr>
                          <tr><td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>4</td><td style={{ border: '1px solid #000', padding: '5px' }}>Category: Govt/Private Sector</td><td style={{ border: '1px solid #000', padding: '5px' }}>{formValues?.category}</td></tr>
                          <tr><td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>5</td><td style={{ border: '1px solid #000', padding: '5px' }}>Sanction number</td><td style={{ border: '1px solid #000', padding: '5px' }}>{formValues?.sanctionNumber}</td></tr>
                          <tr>
                            <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>6</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>Sanctioned Capacity (KW)<br />Installed Capacity (KW)</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>
                              Sanctioned: {formValues?.sanctionedCapacity} KW<br />
                              Installed: {formValues?.installedCapacity} KW
                            </td>
                          </tr>
                          <tr><td colSpan="3" style={{ border: '1px solid #000', padding: '5px', fontWeight: 'bold' }}>Specification of the Modules</td></tr>
                          <tr>
                            <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>7</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>Make of Module<br />ALMM Model Number<br />Wattage per module<br />No. of Module<br />Total Capacity (KWP)<br />Warrantee Details</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>
                              {formValues?.moduleMake}<br />
                              {formValues?.moduleModel}<br />
                              {formValues?.moduleWattage} WP<br />
                              {formValues?.numberOfModules}<br />
                              {formValues?.totalCapacity} KWP<br />
                              {formValues?.warrantyDetails || "N/A"}
                            </td>
                          </tr>
                          <tr><td colSpan="3" style={{ border: '1px solid #000', padding: '5px', fontWeight: 'bold' }}>PCU</td></tr>
                          <tr>
                            <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>8</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>Make & Model of Inverter<br />Rating<br />Type of MPPT<br />Capacity of Inverter<br />HPD<br />Year of manufacturing</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>
                              {formValues?.inverterModel}<br />
                              {formValues?.inverterRating}<br />
                              {formValues?.mppt}<br />
                              {formValues?.inverterCapacity} KW<br />
                              {formValues?.hpd}<br />
                              {formValues?.manufacturingYear}
                            </td>
                          </tr>
                          <tr><td colSpan="3" style={{ border: '1px solid #000', padding: '5px', fontWeight: 'bold' }}>Earthing and Protections</td></tr>
                          <tr>
                            <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>9</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>No of Separate Earthings<br />Earthing Resistance<br />Lightning Arrester</td>
                            <td style={{ border: '1px solid #000', padding: '5px' }}>
                              {formValues?.numberOfEarthings}<br />
                              {formValues?.earthingResistance}<br />
                              {formValues?.lightningArrester}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Declaration */}
                      <div style={{ fontSize: '9pt', marginTop: '15px' }}>
                        <p style={{ textAlign: 'justify' }}>We <strong>{formValues?.vendorName || "Niyati Multi Services"}</strong> [Vendor] & <strong>{formValues?.name}</strong> [Consumer] bearing Consumer Number <strong>{formValues?.consumerNumber}</strong> Ensured structural stability of installed solar power plant and obtained requisite permissions from the concerned authority. If in future, by virtue of any means due to collapsing or damage to installed solar power plant, MSEDCL will not be held responsible for any loss to property or human life, if any.</p>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ height: '40px' }}>
                              <img src={vendorSign} alt="Sign" style={{ height: '100%' }} />
                            </div>
                            <div>Signature [Vendor]</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ height: '40px' }}></div>
                            <div>Signature [Consumer]</div>
                          </div>
                        </div>
                      </div>

                      {/* Page 2 Break Indicator */}
                      <Divider plain><Text type="secondary" style={{ fontSize: 10 }}>Page 2 Section</Text></Divider>

                      <div style={{ textAlign: 'center', margin: '20px 0' }}>
                        <h4 style={{ margin: 0, fontWeight: 'bold' }}>Guarantee Certificate Undertaking</h4>
                      </div>

                      <p style={{ fontSize: '9pt' }}>The undersigned will provide the services to the consumers for repairs/maintenance of the RTS plant free of cost for 5 years...</p>

                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', alignItems: 'flex-end' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ height: '40px' }}>
                            <img src={vendorSign} alt="Sign" style={{ height: '100%' }} />
                          </div>
                          <div>Signature [Vendor]</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ height: '60px' }}>
                            <img src={vendorSeal} alt="Seal" style={{ height: '100%' }} />
                          </div>
                          <div>Stamp & Seal</div>
                        </div>
                      </div>

                      <div style={{ marginTop: '30px' }}>
                        <h4 style={{ fontWeight: 'bold' }}>Identity Details of Consumer:</h4>
                        <p>Aadhar Number: {formValues?.aadharNumber}</p>
                        {aadhaarImageUrls.length > 0 && aadhaarImageUrls.map((url, i) => (
                          <div key={i} style={{ marginTop: 10, border: '1px solid #eee', padding: 5, width: 'fit-content' }}>
                            {url.toLowerCase().endsWith('.pdf') ? (
                              <div style={{ padding: '10px', background: '#f5f5f5', borderRadius: '4px', textAlign: 'center' }}>
                                <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
                                <div style={{ marginTop: 5 }}><a href={url} target="_blank" rel="noopener noreferrer">View Aadhaar PDF {aadhaarImageUrls.length > 1 ? i + 1 : ""}</a></div>
                              </div>
                            ) : (
                              <img src={url} alt={`Aadhaar ${aadhaarImageUrls.length > 1 ? i + 1 : ""}`} style={{ height: '100px', width: 'auto' }} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default MSEBdocsPage;
